import { Geometry } from '@turf/helpers';
import {
  WorkType,
  Authorization,
  Property,
  Product,
  TankInformation,
  TimingEvent,
  ApplicationMethod
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { Applicator } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { CropZoneMatchType } from 'screens/Applications&WorkOrders/Components/FilterDrawer/Constants/filterFormikData';

/**
 * Common interface for both Applications and Work Orders
 * Used by shared components like DetailOverview and DetailsTabs
 */
export interface OperationDetails {
  id: string;
  orgId: string;
  name: string;
  type: WorkType;
  startDate: string;
  endDate?: string; // Optional - Work Orders don't have endDate
  totalCost: number | null | undefined;
  totalAppliedArea: number;
  totalAppliedAreaUnit: string;
  cropSeasonIds: string[];
  authorization: Authorization;
  notes?: string;
  properties: Property[];
  products: Product[];
  tankInformation?: TankInformation;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  timingEvent?: TimingEvent;
  applicationMethod?: ApplicationMethod;
  equipmentIds?: string[];
  applicators?: Applicator[];
  reiExpirationDate?: string;
  phiExpirationDate?: string;
  tagIds?: string[];
  weather?: any; // Optional - only Applications have weather
}

export enum SORT_BY_VALUES {
  START_DATE = 'startDate',
  NAME = 'description',
  CREATION_DATE = 'createdAt'
}
export enum SORT_ORDER_VALUES {
  ASC = 'asc',
  DESC = 'desc'
}

export interface SortConfig {
  sortBy: SORT_BY_VALUES;
  order: SORT_ORDER_VALUES;
}

export interface OperationSummaryParams {
  name?: string;
  fromDate?: string;
  toDate?: string;
  cropZoneIds?: string[];
  cropZoneMatchType?: CropZoneMatchType;
  cropSeasonIds?: string[];
  page?: number;
  size?: number;
  sorts?: SortConfig[];
  filters?: { name?: string };
  id?: string;
}

/** Error payload returned with operation summary responses when the request fails server-side. */
export interface OperationSummaryError {
  message: string;
  code: string;
  timestamp: string;
}

export interface OperationSummaryResponse<T> {
  results: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  currentPage: number;
  numberOfElements: number;
  errors: OperationSummaryError | null;
}

export interface OperationSummary {
  id: string;
  name: string;
  startDate: string;
  fieldsBrief: {
    firstFields: Array<{
      id: string;
      name: string;
    }>;
    firstFieldInfo: {
      id: string;
      name: string;
      geometry: Geometry;
    };
    totalFields: number;
  };
  recordSource?: {
    recordSourceId: string;
    recordSourceType: string;
    recordSourceAdditionalInfo?: string;
  };
  tagIds?: string[];
  [key: string]: any; // Allow for additional API fields
}

export interface PropertyLandingPageGeometry {
  type: 'Polygon' | 'MultiPolygon';
  coordinates: number[][] | number[][][][];
}

export interface PropertyLandingPageCropRef {
  id: string;
  name: string;
}

export interface PropertyLandingPageCropSeason {
  status: number;
  message: string;
  id: string;
  name: string;
  crops: PropertyLandingPageCropRef[];
  currency: string;
  active: boolean;
}

export interface PropertyLandingPageCropZone {
  id: string;
  name: string;
  reportedArea: number;
  boundaryArea: number;
  geometry: PropertyLandingPageGeometry;
  cropSeasons: PropertyLandingPageCropSeason[];
  crop: PropertyLandingPageCropRef;
  archived: boolean;
}

export interface PropertyLandingPageField {
  id: string;
  name: string;
  geometry: PropertyLandingPageGeometry;
  cropzones: PropertyLandingPageCropZone[];
  boundaryArea: number;
  reportedArea: number;
  archived: boolean;
}

export interface PropertyLandingPageItem {
  status: number;
  message: string;
  id: string;
  name: string;
  fields: PropertyLandingPageField[];
  archived: boolean;
}

export interface PropertyLandingPageData {
  properties?: PropertyLandingPageItem[];
}
